import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { CartContext } from '../context/CartContext';
import Toast from './Toast';
import 'react-quill-new/dist/quill.snow.css'; // 👈 DODANO: Import stylów Quill dla poprawnego wyświetlania formatowania
import '../styles/PageProductDetails.css';

function PageProductDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isLoggedIn } = useContext(AuthContext);
    const { fetchCart } = useContext(CartContext);

    const [product, setProduct] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [quantity, setQuantity] = useState(1);
    const [similarProducts, setSimilarProducts] = useState([]);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [isFavorite, setIsFavorite] = useState(false);
    const [toast, setToast] = useState('');
    // Resolve category names for display
    const [categoryNames, setCategoryNames] = useState([]);

    const VAT_RATE = 23;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/products/${id}`);
                setProduct(res.data);
            } catch (error) {
                console.error('Błąd pobierania produktu:', error);
            }
        };
        fetchProduct();
    }, [id]);

    useEffect(() => {
        if (!product) return;
        const fetchSimilar = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/products/${product._id}/similar`
                );
                setSimilarProducts(res.data);
            } catch (error) {
                console.error('Błąd pobierania podobnych produktów:', error);
            }
        };
        fetchSimilar();
    }, [product]);

    // Fetch related products
    useEffect(() => {
        if (!product || !product.relatedProducts || product.relatedProducts.length === 0) {
            setRelatedProducts([]);
            return;
        }

        const fetchRelated = async () => {
            try {
                // Fetch all related products based on their IDs
                const relatedList = await Promise.all(
                    product.relatedProducts.map((item) => {
                        // Handle both ID strings and objects with _id property
                        const id = typeof item === 'string' ? item : item._id;
                        return axios.get(`${process.env.REACT_APP_API_URL}/api/products/${id}`)
                            .then((res) => res.data)
                            .catch(() => null);
                    })
                );
                setRelatedProducts(relatedList.filter((item) => item !== null));
            } catch (error) {
                console.error('Błąd pobierania powiązanych produktów:', error);
            }
        };
        fetchRelated();
    }, [product]);

    // Resolve category names for display
    useEffect(() => {
        if (!product) return;
        const resolveNames = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/categories`);
                const categories = res.data || [];
                
                // Handle multiple categories array
                const resolved = (product.categories || []).map((cat) => {
                    const found = categories.find((c) => c._id === cat.category);
                    return {
                        categoryName: found?.name || '',
                        subCategory: cat.subCategory || ''
                    };
                });
                setCategoryNames(resolved);
            } catch (err) {
                console.error('Błąd pobierania kategorii:', err);
            }
        };
        resolveNames();
    }, [product]);

    // 🔹 Sprawdzenie, czy produkt jest już w ulubionych
    useEffect(() => {
        const checkFavorite = async () => {
            if (!isLoggedIn || !product) return;
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/favorites`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
                });
                const favorites = res.data.items || [];
                setIsFavorite(favorites.some((fav) => fav.product._id === product._id));
            } catch (error) {
                console.error('Błąd pobierania ulubionych:', error);
            }
        };
        checkFavorite();
    }, [isLoggedIn, product]);

    const handlePrev = () => {
        setCurrentImageIndex(
            (prev) => (prev - 1 + (product?.images?.length || 1)) % (product?.images?.length || 1)
        );
    };

    const handleNext = () => {
        setCurrentImageIndex((prev) => (prev + 1) % (product?.images?.length || 1));
    };

    const handleDotClick = (index) => setCurrentImageIndex(index);

    // ✅ Dodawanie produktu do koszyka (z aktualizacją kontekstu)
    const handleAddToCart = async () => {
        if (!isLoggedIn) {
            setToast('Musisz być zalogowany, aby dodać produkt do koszyka.');
            navigate('/login');
            return;
        }

        const available = product.stock - product.reserved;
        if (quantity > available) {
            setToast(`Nie można dodać do koszyka. Dostępna ilość: ${available}`);
            return;
        }

        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/cart/add`,
                { productId: product._id, quantity },
                { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
            );
            // ✅ odśwież globalny stan koszyka
            await fetchCart();

            setToast('✅ Produkt dodany do koszyka!');
        } catch (error) {
            console.error('Błąd dodawania do koszyka:', error);
            setToast('❌ Wystąpił błąd przy dodawaniu do koszyka.');
        }
    };

    // 🔹 Dodaj / usuń z ulubionych
    const handleToggleFavorite = async () => {
        if (!isLoggedIn) {
            setToast('Musisz być zalogowany, aby dodać produkt do ulubionych.');
            navigate('/login');
            return;
        }

        try {
            if (isFavorite) {
                await axios.delete(
                    `${process.env.REACT_APP_API_URL}/api/favorites/remove/${product._id}`,
                    { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
                );
                setIsFavorite(false);
                setToast('💔 Usunięto z ulubionych');
            } else {
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/favorites/add`,
                    { productId: product._id },
                    { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
                );
                setIsFavorite(true);
                setToast('❤️ Dodano do ulubionych!');
            }
        } catch (error) {
            console.error('Błąd przy zmianie statusu ulubionego:', error);
        }
    };

    if (!product)
        return (
            <div className="pp-wrapper">
                <div className="pp-overlay">Ładowanie...</div>
            </div>
        );

    const images =
        Array.isArray(product.images) && product.images.length > 0
            ? product.images
            : ['https://via.placeholder.com/300x200?text=Brak+zdjęcia'];

    const getImgSrc = (img) => {
        if (!img) return img;
        if (typeof img !== 'string') return img;
        if (img.startsWith('http') || img.startsWith('data:')) return img;
        return `${process.env.REACT_APP_API_URL}${img}`;
    };

    const bruttoPrice = Number(product.price).toFixed(2);
    const nettoPrice = (bruttoPrice / (1 + VAT_RATE / 100)).toFixed(2);
    const availableStock = Math.max(0, product.stock - product.reserved);

    return (
        <div className="pp-wrapper">
            <div className="pp-overlay">
                <div className="pp-content-container">
                    <div className="pp-main-section">
                        <div className="pp-left-column">
                            <div className="pp-gallery">
                                <div className="pp-image-container">
                                    <img src={getImgSrc(images[currentImageIndex])} alt="product" className="pp-main-image" />
                                    {images.length > 1 && (
                                        <>
                                            <button className="pp-arrow pp-left" onClick={handlePrev}>
                                                &lt;
                                            </button>
                                            <button className="pp-arrow pp-right" onClick={handleNext}>
                                                &gt;
                                            </button>
                                        </>
                                    )}
                                </div>
                                {images.length > 1 && (
                                    <div className="pp-thumbnails">
                                        {images.map((img, idx) => (
                                            <img
                                                key={idx}
                                                src={getImgSrc(img)}
                                                alt={`thumb-${idx}`}
                                                className={`pp-thumbnail ${idx === currentImageIndex ? 'pp-active' : ''}`}
                                                onClick={() => handleDotClick(idx)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Sekcja opisu rozszerzona o style Quill (ql-container ql-snow) oraz kontener ql-editor */}
                            <div className="pp-description">
                                <h2>Opis produktu</h2>
                                <div className="ql-container ql-snow" style={{ border: 'none' }}>
                                    <div 
                                        className="pp-description-content ql-editor"
                                        style={{ padding: '10px 0' }}
                                        dangerouslySetInnerHTML={{ __html: product.description }} 
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="pp-info">
                            <h1 className="pp-title">{product.name}</h1>
                            
                            {categoryNames.length > 0 && (
                                <div className="pp-meta">
                                    <div className="pp-categories">
                                        <strong>Kategorie:</strong>
                                        <div className="pp-categories-list">
                                            {categoryNames.map((cat, idx) => (
                                                <span key={idx} className="pp-category-item">
                                                    {cat.categoryName}
                                                    {cat.subCategory && ` / ${cat.subCategory}`}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {true && (
                                <div className="pp-pricing-section">
                                    <div className="pp-price-main">
                                        <span className="pp-price-label">Cena:</span>
                                        <span className="pp-price-value">{bruttoPrice} PLN</span>
                                    </div>
                                    
                                    <div className="pp-price-breakdown">
                                        <div className="pp-price-item">
                                            <span>Cena netto:</span>
                                            <span>{nettoPrice} PLN</span>
                                        </div>
                                        <div className="pp-price-item">
                                            <span>VAT (23%):</span>
                                            <span>{(bruttoPrice - nettoPrice).toFixed(2)} PLN</span>
                                        </div>
                                    </div>

                                        <div className="pp-price-promo">
                                        {product.lowestPriceLast30Days && Number(product.lowestPriceLast30Days) < Number(bruttoPrice) && (
                                            <span className="pp-promo-label">📉 Oferta!</span>
                                        )}
                                            <span className="pp-promo-text">Najniższa cena z ostatnich 30 dni: {Number(product.lowestPriceLast30Days).toFixed(2)} PLN</span>
                                        </div>

                                    <div className="pp-stock-info">
                                        <span className={`pp-stock-status ${availableStock > 5 ? 'pp-stock-good' : availableStock > 0 ? 'pp-stock-low' : 'pp-stock-none'}`}>
                                            {availableStock > 0 ? `✓ ${availableStock} szt. dostępnych` : '✗ Brak w magazynie'}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {product.colors && product.colors.length > 0 && (
                                <div className="pp-colors">
                                    <strong>🎨 Kolory produktu:</strong>
                                    <div className="pp-color-list">
                                        {product.colors.map((color, idx) => (
                                            <span key={idx} className="pp-color-badge">{color}</span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="pp-controls">
                                <div className="pp-quantity">
                                    <label htmlFor="quantity">Ilość:</label>
                                    <input
                                        id="quantity"
                                        type="number"
                                        min="1"
                                        max={availableStock}
                                        value={quantity}
                                        onChange={(e) =>
                                            setQuantity(Math.min(parseInt(e.target.value) || 1, availableStock))
                                        }
                                    />
                                </div>

                                <div className="pp-actions">
                                    <button
                                        className="pp-buy-btn"
                                        onClick={handleAddToCart}
                                        disabled={availableStock === 0}
                                    >
                                        {availableStock === 0 ? '✗ Brak w magazynie' : '🛒 Dodaj do koszyka'}
                                    </button>

                                    <button
                                        className={`pp-fav-btn ${isFavorite ? 'active' : ''}`}
                                        onClick={handleToggleFavorite}
                                    >
                                        {isFavorite ? '❤️ Usuń z ulubionych' : '🤍 Dodaj do ulubionych'}
                                    </button>
                                </div>
                            </div>

                            {(product.height != null || product.width != null || product.depth != null || product.thickness != null || product.length != null || product.weight != null) && (
                                    <div className="pp-dimensions">
                                        <h3>📏 Wymiary i waga produktu</h3>
                                        <div className="pp-dimensions-grid">
                                            {product.height != null && <div className="pp-dim-item"><span>Wysokość:</span> <strong>{Number(product.height).toFixed(1)} cm</strong></div>}
                                            {product.width != null && <div className="pp-dim-item"><span>Szerokość:</span> <strong>{Number(product.width).toFixed(1)} cm</strong></div>}
                                            {product.depth != null && <div className="pp-dim-item"><span>Głębokość:</span> <strong>{Number(product.depth).toFixed(1)} cm</strong></div>}
                                            {product.thickness != null && <div className="pp-dim-item"><span>Grubość:</span> <strong>{Number(product.thickness).toFixed(1)} cm</strong></div>}
                                            {product.length != null && <div className="pp-dim-item"><span>Długość:</span> <strong>{Number(product.length).toFixed(1)} cm</strong></div>}
                                            {product.weight != null && <div className="pp-dim-item"><span>Waga:</span> <strong>{Number(product.weight).toFixed(1)} kg</strong></div>}
                                        </div>
                                    </div>
                            )}
                        </div>
                    </div>

                    {relatedProducts.length > 0 && (
                        <div className="pp-related-products">
                            <h2>🔗 Powiązane produkty</h2>
                            <div className="pp-related-grid">
                                {relatedProducts.map((item) => (
                                    <div
                                        className="pp-related-card"
                                        key={item._id}
                                        onClick={() => navigate(`/products/${item._id}`)}
                                    >
                                        <img
                                            src={item.images?.[0] || 'https://via.placeholder.com/150?text=Brak+zdjęcia'}
                                            alt={item.name}
                                        />
                                        <p className="pp-related-name">{item.name}</p>
                                        <p className="pp-related-price">{Number(item.price).toFixed(2)} PLN</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="pp-similar-products">
                        <h2>Podobne produkty</h2>
                        <div className="pp-similar-grid">
                            {similarProducts.length > 0 ? (
                                similarProducts.map((item) => (
                                    <div
                                        className="pp-similar-card"
                                        key={item._id}
                                        onClick={() => navigate(`/products/${item._id}`)}
                                    >
                                        <img
                                            src={item.images?.[0] || 'https://via.placeholder.com/150?text=Brak+zdjęcia'}
                                            alt={item.name}
                                        />
                                        <p>{item.name}</p>
                                    </div>
                                ))
                            ) : (
                                <p>Brak podobnych produktów.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {toast && <Toast message={toast} onClose={() => setToast('')} />}
        </div>
    );
}

export default PageProductDetails;